// src/tareas/CrearTarea.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// Toast muy sencillo
const Toast = ({ type = "info", children }) => {
  if (!children) return null;
  const base = {
    position: "fixed",
    top: 80,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9999,
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 600,
    boxShadow: "0 6px 20px rgba(0,0,0,.12)",
    maxWidth: "90vw",
    textAlign: "center",
  };
  const variants = {
    ok: {
      background: "#e7f8ed",
      color: "#0f5132",
      border: "1px solid #b6f0c6",
    },
    error: {
      background: "#fdeceb",
      color: "#842029",
      border: "1px solid #f5c2c7",
    },
    info: {
      background: "#e8f6ff",
      color: "#084298",
      border: "1px solid #b6d9ff",
    },
  };
  return <div style={{ ...base, ...(variants[type] || {}) }}>{children}</div>;
};

export const CrearTarea = () => {
  const { auth } = useAuth();
  const creadorId = auth?.id;

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState("normal"); // "baja" | "normal" | "alta" | "critica"
  const [fechaLimite, setFechaLimite] = useState("");

  const [departamentos, setDepartamentos] = useState([]);
  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState(null);

  const [usuariosDepto, setUsuariosDepto] = useState([]);
  const [destUsuarios, setDestUsuarios] = useState([]); // react-select (array de options)

  const [cargando, setCargando] = useState(false);
  const [cargandoDeptos, setCargandoDeptos] = useState(false);
  const [cargandoUsuariosDepto, setCargandoUsuariosDepto] = useState(false);

  const [toast, setToast] = useState(null);

  const mostrarToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const authHeadersJson = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // 1) Cargar departamentos activos
  useEffect(() => {
    const fetchDeptos = async () => {
      setCargandoDeptos(true);
      try {
        const resDept = await fetch(`${Global.url}departamentos`, {
          headers: authHeaders(),
        });
        if (resDept.ok) {
          const dataDept = await resDept.json();
          const lista = Array.isArray(dataDept)
            ? dataDept.filter((d) => d.activo !== false)
            : [];
          setDepartamentos(lista);
        }
      } catch (e) {
        console.error("Error cargando departamentos para tareas:", e);
      } finally {
        setCargandoDeptos(false);
      }
    };
    fetchDeptos();
  }, []);

  // 2) Cuando cambia el departamento seleccionado, cargamos sus usuarios
  useEffect(() => {
    if (!departamentoSeleccionado) {
      setUsuariosDepto([]);
      setDestUsuarios([]);
      return;
    }

    const fetchUsuariosDepto = async () => {
      setCargandoUsuariosDepto(true);
      try {
        const res = await fetch(
          `${Global.url}departamentos/${departamentoSeleccionado}/usuarios`,
          { headers: authHeaders() }
        );
        if (res.ok) {
          const data = await res.json();
          // Solo miembros activos
          const lista = Array.isArray(data)
            ? data.filter((u) => u.miembro_activo !== false)
            : [];
          setUsuariosDepto(lista);
          setDestUsuarios([]);
        }
      } catch (e) {
        console.error(
          "Error cargando usuarios del departamento para tareas:",
          e
        );
      } finally {
        setCargandoUsuariosDepto(false);
      }
    };

    fetchUsuariosDepto();
  }, [departamentoSeleccionado]);

  // Opciones para react-select

  const departamentoOptions = useMemo(
    () =>
      (departamentos || [])
        .map((d) => ({
          value: String(d.id),
          label: d.nombre || `Depto #${d.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [departamentos]
  );

  const usuarioOptions = useMemo(
    () =>
      (usuariosDepto || [])
        .map((u) => ({
          value: String(u.id),
          label: `${u.nombre || ""} ${u.apellidos || ""} (${
            u.email || u.dni || "s/d"
          })`.trim(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [usuariosDepto]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cargando) return;

    if (!creadorId) {
      mostrarToast(
        "error",
        "No se pudo determinar el usuario creador (auth.id)."
      );
      return;
    }

    if (!titulo.trim() || !descripcion.trim()) {
      mostrarToast("error", "Título y descripción son obligatorios.");
      return;
    }

    if (!departamentoSeleccionado) {
      mostrarToast(
        "error",
        "Debes seleccionar un departamento responsable de la tarea."
      );
      return;
    }

    const destinatariosUsuarios = destUsuarios.map((opt) =>
      Number(opt.value)
    );

    // 🔵 Siempre va a la cola del departamento
    const destinatariosDepartamentos = [Number(departamentoSeleccionado)];

    const payload = {
      titulo: titulo.trim(),
      descripcion: descripcion.trim(),
      prioridad,
      fecha_vencimiento: fechaLimite || null,
      departamento_id: Number(departamentoSeleccionado),
      destinatarios_usuarios: destinatariosUsuarios,      // [idUsuario] (si los hay)
      destinatarios_departamentos: destinatariosDepartamentos, // siempre [departamento_id]
    };

    try {
      setCargando(true);
      const res = await fetch(`${Global.url}tareas`, {
        method: "POST",
        headers: authHeadersJson(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Error al crear tarea:", data);
        mostrarToast("error", data.message || "No se pudo crear la tarea.");
        return;
      }

      // Éxito
      mostrarToast("ok", "Tarea creada correctamente.");
      setTitulo("");
      setDescripcion("");
      setPrioridad("normal");
      setFechaLimite("");
      setDestUsuarios([]);
      // Mantenemos el departamento seleccionado para crear varias tareas seguidas en el mismo contexto
    } catch (error) {
      console.error("Error al crear tarea:", error);
      mostrarToast("error", "Error de red al crear la tarea.");
    } finally {
      setCargando(false);
    }
  };

  const selectedDeptOption =
    departamentoOptions.find(
      (opt) => Number(opt.value) === Number(departamentoSeleccionado)
    ) || null;

  return (
    <div className="form-orden">
      {toast && <Toast type={toast.type}>{toast.text}</Toast>}

      <h2>Crear nueva tarea</h2>

      <form className="registro-fecha" onSubmit={handleSubmit}>
        {/* Departamento responsable */}
        <label>Departamento responsable:</label>
        {cargandoDeptos ? (
          <p>Cargando departamentos…</p>
        ) : (
          <Select
            classNamePrefix="select"
            placeholder="Selecciona un departamento…"
            options={departamentoOptions}
            value={selectedDeptOption}
            onChange={(opt) =>
              setDepartamentoSeleccionado(opt ? Number(opt.value) : null)
            }
            isClearable
          />
        )}

        {!departamentoSeleccionado && (
          <p style={{ fontSize: "0.85rem", color: "#666", marginTop: 4 }}>
            Primero selecciona un departamento para poder elegir destinatarios.
          </p>
        )}

        <label style={{ marginTop: 12 }}>Título de la tarea:</label>
        <input
          className="input-layout"
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej: Revisar informe mensual"
        />

        <label>Descripción:</label>
        <textarea
          className="input-layout"
          rows={4}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Describe qué se necesita hacer, contexto, detalles..."
        />

        <div className="form-group-inline">
          <div style={{ flex: 1, marginRight: 8 }}>
            <label>Prioridad:</label>
            <select
              className="input-layout"
              value={prioridad}
              onChange={(e) => setPrioridad(e.target.value)}
            >
              <option value="baja">Baja</option>
              <option value="normal">Media</option>
              <option value="alta">Alta</option>
              <option value="critica">Crítica</option>
            </select>
          </div>

          <div style={{ flex: 1, marginLeft: 8 }}>
            <label>Fecha límite (opcional):</label>
            <input
              className="input-layout"
              type="date"
              value={fechaLimite}
              onChange={(e) => setFechaLimite(e.target.value)}
            />
          </div>
        </div>

        {/* Destinatarios usuarios (del departamento) */}
        <label style={{ marginTop: 12 }}>
          Destinatarios (usuarios del departamento):
        </label>
        {cargandoUsuariosDepto ? (
          <p>Cargando usuarios del departamento…</p>
        ) : (
          <Select
            isMulti
            isSearchable
            isDisabled={!departamentoSeleccionado || usuariosDepto.length === 0}
            classNamePrefix="select"
            placeholder={
              !departamentoSeleccionado
                ? "Selecciona primero un departamento…"
                : usuariosDepto.length === 0
                ? "Este departamento no tiene usuarios activos."
                : "Selecciona uno o varios usuarios…"
            }
            options={usuarioOptions}
            value={destUsuarios}
            onChange={(opts) => setDestUsuarios(opts || [])}
          />
        )}

        <p style={{ fontSize: "0.85rem", color: "#666", marginTop: 8 }}>
          La tarea se enviará <strong>siempre</strong> a la cola del
          departamento seleccionado. Si además eliges uno o varios usuarios,
          aparecerá asociada a ellos en sus tableros personales.
        </p>

        <button
          type="submit"
          className="btn-login"
          style={{ marginTop: 16 }}
          disabled={cargando}
        >
          {cargando ? "Creando tarea..." : "Crear Tarea"}
        </button>
      </form>
    </div>
  );
};

export default CrearTarea;
